import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, Check, X, Plus, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";

const emptyReq = { item_name: "", item_id: null, qty: 1, customer_name: "", customer_phone: "", notes: "" };

export function ReminderBanner() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/purchase-reminders").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-6" data-testid="reminder-banner">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 overline text-violet-900"><BellRing size={14} /> You have to purchase these items ({rows.length}) — customers asked for them</div>
        <Button size="sm" variant="outline" onClick={() => nav("/app/item-requests")}>Manage</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.slice(0, 12).map((r) => (
          <span key={r._id_str} className="chip bg-white border border-violet-200 text-violet-900" data-testid={`reminder-chip-${r._id_str}`}>
            {r.item_name} × {r.qty}{r.customer_name ? ` · for ${r.customer_name}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

export async function addReminder(data) {
  await api.post("/purchase-reminders", data);
  toast.success(`Noted: purchase "${data.item_name}"`);
}

export default function PurchaseReminders() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [f, setF] = useState(emptyReq);
  const [items, setItems] = useState([]);

  const load = async () => { const { data } = await api.get(`/purchase-reminders?status=${status}`); setRows(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { api.get("/items").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  const save = async () => {
    if (!f.item_name.trim()) return toast.error("Enter the item customer asked for");
    const match = items.find((i) => i.name.toLowerCase() === f.item_name.trim().toLowerCase());
    await addReminder({ ...f, item_id: match?._id_str || null });
    setF(emptyReq); load();
  };
  const setSt = async (id, st) => { await api.post(`/purchase-reminders/${id}/status`, { status: st }); load(); };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="overline">Purchase Reminders</div>
          <h1 className="text-3xl font-bold tracking-tight">Item Requests</h1>
          <p className="text-sm text-muted-foreground mt-1">Note items customers asked for that you don't have. They appear on the Purchase pages until you buy them.</p>
        </div>
        <Button className="btn-primary" onClick={() => nav("/app/purchase-orders?new=1")} data-testid="req-create-po"><ShoppingBag size={14} /> Create Purchase Order</Button>
      </div>
      <div className="bg-white border border-border rounded-lg p-4 mb-6 grid md:grid-cols-6 gap-3 items-end">
        <div className="md:col-span-2"><Label>Item asked for *</Label>
          <Input list="req-items" value={f.item_name} onChange={(e) => setF({ ...f, item_name: e.target.value })} placeholder="e.g. Dettol 500ml" data-testid="req-item-input" />
          <datalist id="req-items">{items.map((i) => <option key={i._id_str} value={i.name} />)}</datalist>
        </div>
        <div><Label>Qty</Label><Input type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: +e.target.value })} data-testid="req-qty-input" /></div>
        <div><Label>Customer</Label><Input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="req-customer-input" /></div>
        <div><Label>Phone</Label><Input value={f.customer_phone} onChange={(e) => setF({ ...f, customer_phone: e.target.value })} /></div>
        <Button className="btn-primary" onClick={save} data-testid="req-add-btn"><Plus size={14} /> Note it</Button>
        <div className="md:col-span-6"><Input placeholder="Notes (brand, size, urgency…)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
      </div>
      <div className="flex gap-2 mb-3">
        {["open", "purchased", "dismissed"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`req-tab-${s}`}
            className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-md border ${status === s ? "border-[#064E3B] bg-[#064E3B] text-white" : "border-border bg-white"}`}>{s}</button>
        ))}
      </div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="acc-table w-full">
          <thead><tr><th>Noted on</th><th>Item</th><th className="num">Qty</th><th>Customer</th><th>Notes</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Nothing here.</td></tr>}
            {rows.map((r) => (
              <tr key={r._id_str} data-testid={`req-row-${r._id_str}`}>
                <td>{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="font-semibold">{r.item_name}</td>
                <td className="num">{r.qty}</td>
                <td>{r.customer_name || "—"} {r.customer_phone && <span className="text-xs text-slate-500">{r.customer_phone}</span>}</td>
                <td className="text-xs">{r.notes || "—"}</td>
                <td>
                  {r.status === "open" && (
                    <div className="flex gap-1">
                      <button className="p-1 hover:bg-emerald-50 text-emerald-700 rounded" title="Mark purchased" onClick={() => setSt(r._id_str, "purchased")} data-testid={`req-done-${r._id_str}`}><Check size={14} /></button>
                      <button className="p-1 hover:bg-red-50 text-red-600 rounded" title="Dismiss" onClick={() => setSt(r._id_str, "dismissed")} data-testid={`req-dismiss-${r._id_str}`}><X size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
